import React, { useRef, useState } from 'react';
import { studentShorts } from '../mock';
import { Play } from 'lucide-react';

// Renders a YouTube Short with video-only chrome:
// - Loads only on click (lazy) to avoid 6 iframes hammering the page
// - Hides title bar/branding using disablekb, modestbranding, rel=0, fs=0, etc.
const ShortCard = ({ id, index, onOpen }) => {
  // Rotated, staggered, fun layout
  const offsets = [
    'md:translate-y-0 rotate-[-2deg]',
    'md:translate-y-6 rotate-[1deg]',
    'md:-translate-y-3 rotate-[2deg]',
    'md:translate-y-8 rotate-[-1deg]',
    'md:-translate-y-2 rotate-[1.5deg]',
    'md:translate-y-4 rotate-[-1.5deg]',
  ];
  const tilt = offsets[index % offsets.length];

  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      data-testid={`student-short-${id}`}
      className={`group relative shrink-0 w-[220px] md:w-[260px] aspect-[9/16] rounded-3xl overflow-hidden bg-slate-900 shadow-2xl ring-1 ring-white/10 hover:ring-purple-400/60 transition-all duration-500 transform ${tilt} hover:rotate-0 hover:scale-105`}
    >
      {/* Poster */}
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt="MatchFrame student review"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover scale-[1.4] group-hover:scale-[1.5] transition-transform duration-700"
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/15 backdrop-blur-md border border-white/40 flex items-center justify-center group-hover:bg-purple-600 group-hover:border-purple-300 transition-all duration-300 shadow-lg">
          <Play className="h-7 w-7 md:h-9 md:w-9 text-white fill-white ml-1" />
        </div>
      </div>

      {/* Sticker label */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
        <span className="px-2.5 py-1 bg-purple-600/90 text-white text-[10px] font-bold uppercase tracking-wider rounded-md shadow">
          Student Review
        </span>
        <span className="text-white/80 text-[10px] font-mono">#{String(index + 1).padStart(2, '0')}</span>
      </div>
    </button>
  );
};

const ShortModal = ({ id, onClose }) => {
  if (!id) return null;
  // youtube-nocookie has more permissive embedding for Shorts.
  const src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&modestbranding=1&rel=0&fs=0&disablekb=1&controls=1&iv_load_policy=3&playsinline=1`;
  const watchUrl = `https://www.youtube.com/shorts/${id}`;
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="student-short-modal"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl"
      >
        ×
      </button>
      <div
        className="relative w-full max-w-[420px] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full aspect-[9/16] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-black">
          <iframe
            src={src}
            title="MatchFrame student review"
            allow="autoplay; encrypted-media; picture-in-picture; clipboard-write"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 w-full h-full"
            style={{ border: 0 }}
          />
        </div>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-full transition-all"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-red-500"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          If video doesn&apos;t play, watch on YouTube
        </a>
      </div>
    </div>
  );
};

export const StudentShorts = () => {
  const [openId, setOpenId] = useState(null);
  const scrollerRef = useRef(null);

  return (
    <section className="relative py-20 overflow-hidden bg-slate-950">
      {/* subtle brand-aligned accents (no harsh gradients) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-900/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-900/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-5 py-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-white/80 font-semibold text-xs tracking-widest uppercase">In their own words</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl mx-auto leading-tight">
            Alumni &amp; Student Experiences
            <span className="block bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent mt-2">
              at MatchFrame Editing School Bengaluru
            </span>
          </h2>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
            Real reviews from MatchFrame students. Tap any clip to play.
          </p>
        </div>

        {/* Horizontal scroller */}
        <div
          ref={scrollerRef}
          className="flex gap-6 md:gap-10 overflow-x-auto pb-12 px-2 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {studentShorts.map((s, i) => (
            <div key={s.id} className="snap-start">
              <ShortCard id={s.id} index={i} onOpen={setOpenId} />
            </div>
          ))}
        </div>
      </div>

      <ShortModal id={openId} onClose={() => setOpenId(null)} />
    </section>
  );
};
